import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Language } from '../types';
import { theme } from '../styles/theme';

const LanguageSelectorContainer = styled.div`
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const LanguageButton = styled.button<{ isActive: boolean }>`
  padding: ${theme.spacing(1)} ${theme.spacing(2)};
  border: 2px solid ${props => props.isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: ${theme.shape.borderRadius}px;
  background: ${props => props.isActive 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${theme.colors.onPrimary};
  font-size: ${theme.typography.body2.fontSize};
  font-weight: ${theme.typography.subtitle2.fontWeight};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 48px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(0.5)} ${theme.spacing(1)};
    font-size: ${theme.typography.caption.fontSize};
    min-width: 44px;
  }
`;

interface LanguageSelectorProps {
  currentLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange
}) => {
  const { i18n } = useTranslation();

  const languages: { code: Language; label: string }[] = [
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
    { code: 'sv', label: 'SV' }
  ];

  const handleLanguageChange = (language: Language) => {
    onLanguageChange(language);
    i18n.changeLanguage(language);
  };

  return (
    <LanguageSelectorContainer>
      {languages.map(({ code, label }) => (
        <LanguageButton
          key={code}
          isActive={currentLanguage === code}
          onClick={() => handleLanguageChange(code)}
        >
          {label}
        </LanguageButton>
      ))}
    </LanguageSelectorContainer>
  );
};

export default LanguageSelector;